/**
 * Estado canónico do hotel escolhido (módulo puro, sem IO, sem React).
 *
 * Regras vinculativas:
 * - só existe hotel quando a pessoa o escolheu explicitamente (`status: "selected"`);
 * - texto "hotel" no pedido ou uma sugestão de alojamento NUNCA são prova de seleção;
 * - sem hotel selecionado não há check-in, check-out, bagagem nem transferências
 *   aeroporto ↔ hotel;
 * - o hotel selecionado nunca é uma visita nem uma paragem numerada;
 * - só entra no orçamento com preço real disponível.
 */

export type HotelSelection =
  | { status: "none" }
  | {
      status: "selected";
      placeId: string;
      name: string;
      latitude: number;
      longitude: number;
      address?: string;
      nightlyPrice?: number;
    };

export const NO_HOTEL_SELECTED: HotelSelection = { status: "none" };

/** Sugestão de alojamento já recolhida (não é seleção). */
export type HotelSuggestion = {
  name: string;
  placeId?: string;
  lat?: number;
  lng?: number;
  address?: string;
  priceLocal?: number;
};

export type SelectHotelResult =
  | { status: "selected"; selection: HotelSelection }
  /** Falta informação essencial: uma única chamada a Place Details, após clique. */
  | { status: "needs_details"; placeId: string; name: string }
  | { status: "rejected"; reason: "missing_place_id" };

/**
 * Classificação ESTRUTURAL de alojamento. Nunca por nome: dois locais podem
 * chamar-se quase igual ("The New Yorker" vs "Wyndham New Yorker Hotel") e
 * apenas os tipos/origem do candidato provam que é alojamento.
 */
const LODGING_TYPES = new Set([
  "lodging",
  "hotel",
  "motel",
  "hostel",
  "resort_hotel",
  "bed_and_breakfast",
  "guest_house",
  "inn",
  "extended_stay_hotel",
  "budget_japanese_inn",
  "japanese_inn",
  "campground",
  "cottage",
  "farmstay",
  "private_guest_room",
]);

export type LodgingCandidate = {
  kind?: string | null;
  source?: string | null;
  placePrimaryType?: string | null;
  placeTypes?: readonly string[] | null;
};

/** `true` quando o candidato é alojamento segundo tipo/origem estruturados. */
export function isLodgingCandidate(candidate: LodgingCandidate): boolean {
  if (candidate.kind === "hotel") return true;
  if (candidate.source === "hotel_suggestion") return true;
  if (candidate.placePrimaryType && LODGING_TYPES.has(candidate.placePrimaryType)) return true;
  return (candidate.placeTypes ?? []).some((t) => LODGING_TYPES.has(String(t)));
}

const isFiniteNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/**
 * Escolhe um hotel JÁ sugerido, usando exclusivamente os dados existentes.
 * Nunca faz recolha, IA ou pesquisa: devolve `needs_details` quando faltam
 * coordenadas e é a interface que pede Place Details depois do clique.
 */
export function selectSuggestedHotel(suggestion: HotelSuggestion): SelectHotelResult {
  const placeId = (suggestion.placeId ?? "").trim();
  const name = (suggestion.name ?? "").trim();
  if (!placeId || !name) return { status: "rejected", reason: "missing_place_id" };
  if (!isFiniteNumber(suggestion.lat) || !isFiniteNumber(suggestion.lng)) {
    return { status: "needs_details", placeId, name };
  }
  return {
    status: "selected",
    selection: {
      status: "selected",
      placeId,
      name,
      latitude: suggestion.lat,
      longitude: suggestion.lng,
      ...(suggestion.address ? { address: suggestion.address } : {}),
      ...(isFiniteNumber(suggestion.priceLocal) && suggestion.priceLocal > 0
        ? { nightlyPrice: suggestion.priceLocal }
        : {}),
    },
  };
}

/** Remover a seleção — a app volta a poder sugerir hotéis sem inventar logística. */
export function clearHotelSelection(): HotelSelection {
  return { status: "none" };
}

export function isHotelSelected(selection: HotelSelection | null | undefined): boolean {
  return selection?.status === "selected";
}

/** Única origem possível de `hotelPlaceId` em toda a app. */
export function hotelPlaceIdOf(selection: HotelSelection | null | undefined): string | undefined {
  return selection?.status === "selected" ? selection.placeId : undefined;
}

/** Coordenadas do hotel para as pernas de chegada/saída. */
export function hotelCoordsOf(
  selection: HotelSelection | null | undefined,
): { lat: number; lng: number } | null {
  return selection?.status === "selected"
    ? { lat: selection.latitude, lng: selection.longitude }
    : null;
}

/** Preço só entra no orçamento quando é real. */
export function hotelNightlyPrice(selection: HotelSelection | null | undefined): number {
  return selection?.status === "selected" && isFiniteNumber(selection.nightlyPrice)
    ? selection.nightlyPrice
    : 0;
}

/** Assinatura do hotel para a comparação de revisões publicadas. */
export function hotelSelectionSignature(selection: HotelSelection | null | undefined): string {
  if (!selection || selection.status !== "selected") return "hotel:none";
  return [
    "hotel",
    selection.placeId,
    selection.name,
    selection.latitude.toFixed(6),
    selection.longitude.toFixed(6),
    selection.nightlyPrice ?? "",
  ].join(":");
}

/** O hotel escolhido nunca pode aparecer como paragem turística. */
export function excludeHotelFromStops<T extends { placeId?: string | null } & LodgingCandidate>(
  stops: readonly T[],
  selection: HotelSelection | null | undefined,
): T[] {
  // Identidade do hotel escolhido: SEMPRE `placeId`. Sem `placeId` não há
  // seleção (ver `selectSuggestedHotel`), logo não há exclusão por nome.
  const placeId = hotelPlaceIdOf(selection);
  // Qualquer alojamento é excluído das visitas turísticas — por tipo/origem,
  // nunca por semelhança de nome.
  return stops.filter((s) => (placeId ? (s.placeId ?? "") !== placeId : true) && !isLodgingCandidate(s));
}

/**
 * Alojamento ativo: só o hotel escolhido. As sugestões continuam visíveis, mas
 * como alternativas — nunca como "Dormir" nem como entrada temporal.
 */
export function activeAccommodation<T extends HotelSuggestion>(
  selection: HotelSelection | null | undefined,
  suggestions: readonly T[],
): { active: HotelSelection | null; alternatives: T[] } {
  if (!isHotelSelected(selection)) return { active: null, alternatives: [...suggestions] };
  const placeId = hotelPlaceIdOf(selection);
  return {
    active: selection as HotelSelection,
    alternatives: suggestions.filter((s) => (s.placeId ?? "") !== placeId),
  };
}

/** Logística dependente de hotel (check-in/out e transferências do hotel). */
const HOTEL_DEPENDENT_KINDS = new Set(["check_in", "check_out", "airport_transfer"]);

/**
 * Ao alterar/remover o hotel só se removem as entradas logísticas que dele
 * dependem — visitas, refeições e IDs lógicos mantêm-se.
 */
export function dropHotelDependentEntries<T extends { kind?: string }>(entries: readonly T[]): T[] {
  return entries.filter((e) => !HOTEL_DEPENDENT_KINDS.has(String(e.kind)));
}
